import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { patchJSON } from '@/lib/api'

type Chapter = { id: number; name: string }
export type ChaptersResponse = { chapters: Chapter[]; available_chapters: Chapter[] }

export function MemberPortalChapters({ initialData }: { initialData?: ChaptersResponse }) {
  const [data, setData] = useState<ChaptersResponse>(
    initialData ?? { chapters: [], available_chapters: [] },
  )
  const [editing, setEditing] = useState(false)
  const [selected, setSelected] = useState<number[]>([])
  const update = useMutation({
    mutationFn: (chapterIds: number[]) =>
      patchJSON<ChaptersResponse>('/api/v1/member_portal/chapters', {
        chapters: { chapter_ids: chapterIds },
      }),
    onSuccess: (data) => {
      setData(data)
      setEditing(false)
    },
  })
  useEffect(() => {
    if (initialData) setData(initialData)
  }, [initialData])

  const current = data.chapters ?? []
  const available = data.available_chapters ?? []
  const beginEditing = () => {
    setSelected(current.map((chapter) => chapter.id))
    setEditing(true)
  }
  const cancel = () => {
    setSelected(current.map((chapter) => chapter.id))
    setEditing(false)
  }
  const toggle = (id: number) => {
    setSelected((ids) => (ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id]))
  }

  return (
    <ChaptersSection editing={editing} onEdit={beginEditing}>
      {!editing && current.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {current.map((chapter) => (
            <Badge key={chapter.id} className="bg-primary/10 text-primary">
              {chapter.name}
            </Badge>
          ))}
        </div>
      )}
      {editing && (
        <>
          <div>
            {available.map((chapter) => (
              <div key={chapter.id} className="flex items-center gap-2 py-2">
                <Checkbox
                  id={`member-chapter-${chapter.id}`}
                  checked={selected.includes(chapter.id)}
                  onCheckedChange={() => toggle(chapter.id)}
                />
                <Label
                  htmlFor={`member-chapter-${chapter.id}`}
                  className="text-body-sm font-normal"
                >
                  {chapter.name}
                </Label>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button
              type="button"
              size="sm"
              onClick={() => update.mutate(selected)}
              disabled={update.isPending}
            >
              {update.isPending ? 'Saving...' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={cancel}
              disabled={update.isPending}
            >
              Cancel
            </Button>
          </div>
          {update.isError && (
            <p className="mt-2 text-body-sm text-destructive" role="alert">
              Unable to save chapters.
            </p>
          )}
        </>
      )}
    </ChaptersSection>
  )
}

function ChaptersSection({
  editing,
  onEdit,
  children,
}: {
  editing: boolean
  onEdit: () => void
  children: React.ReactNode
}) {
  return (
    <section className="border-t border-border">
      <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6">
        <h2 className="text-body font-medium text-foreground">Chapters</h2>
        {!editing && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Edit Chapters"
            onClick={onEdit}
          >
            <Pencil className="size-4" aria-hidden="true" />
          </Button>
        )}
      </div>
      <div className="px-5 pb-5 sm:px-6 sm:pb-6">{children}</div>
    </section>
  )
}
